import Slider from "react-slick";
import { ChevronLeft, ChevronRight, Plus } from "react-bootstrap-icons";
import { formatCurrency } from "../../utilities";
import { useCart, useProductPrice, useStore } from "../../hooks";
import { useMemo, useState } from "react";
import { toAbsoluteUrl } from "../../helpers";
import ProductAddOnModal from "../product/_modals/ProductAddOnModal";
import { StoreProductModel } from "../../models";
import { Modal } from "react-bootstrap";

const sliderSettings = {
  dots: true,
  arrows: false,
  infinite: true,
  speed: 350,
  slidesToShow: 2.5,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 3500,
  pauseOnHover: true,
  swipe: true,
  touchMove: true,
  draggable: true,
  swipeToSlide: true,
  nextArrow: <NextArrow />,
  prevArrow: <PrevArrow />,
  responsive: [
    {
      breakpoint: 768,
      settings: {
        slidesToShow: 2.35,
        slidesToScroll: 1,
      },
    },
    {
      breakpoint: 576,
      settings: {
        slidesToShow: 3.45,
        slidesToScroll: 1,
      },
    },
    {
      breakpoint: 420,
      settings: {
        slidesToShow: 2.35,
        slidesToScroll: 1,
      },
    },
  ],
};

function PrevArrow(props: any) {
  const { className, style, onClick } = props;
  return (
    <button
      type="button"
      className={className}
      style={{
        ...style,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={onClick}
      aria-label="Previous add-on"
    >
      <ChevronLeft size={18} />
    </button>
  );
}

function NextArrow(props: any) {
  const { className, style, onClick } = props;
  return (
    <button
      type="button"
      className={className}
      style={{
        ...style,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={onClick}
      aria-label="Next add-on"
    >
      <ChevronRight size={18} />
    </button>
  );
}

// Separate component to properly use hooks
const CartSuggestionCard = ({
  item,
  onAddClick,
}: {
  item: StoreProductModel;
  onAddClick: (item: StoreProductModel) => void;
}) => {
  const productPrice = useProductPrice(item);

  return (
    <div className="cart-suggestion-slide w-100">
      <div
        className="suggestion-card cursor-pointer"
        onClick={() => onAddClick(item)}
      >
        <div className="suggestion-image">
          <img
            src={
              item.product_image
                ? item.product_image
                : toAbsoluteUrl("images/av5c.png")
            }
            alt={item.product_name}
          />
        </div>

        <div className="suggestion-info">
          <div className="suggestion-name" title={item.product_name}>
            {item.product_name}
          </div>
          <div className="suggestion-price">{formatCurrency(productPrice)}</div>
        </div>

        <button
          className="suggestion-add-btn"
          onClick={(e) => {
            e.stopPropagation();
            onAddClick(item);
          }}
        >
          <Plus size={18} />
        </button>
      </div>
    </div>
  );
};

// Separate component for addon grid card
const AddonGridCard = ({
  item,
  onAddClick,
}: {
  item: StoreProductModel;
  onAddClick: (item: StoreProductModel) => void;
}) => {
  const productPrice = useProductPrice(item);

  return (
    <div className="addon-grid-card">
      <img
        src={
          item.product_image
            ? item.product_image
            : toAbsoluteUrl("images/av5c.png")
        }
        alt={item.product_name}
      />

      <div className="addon-grid-content">
        <h6>{item.product_name}</h6>

        <span>{formatCurrency(productPrice)}</span>

        <button className="suggestion-add-btn" onClick={() => onAddClick(item)}>
          <Plus size={18} />
        </button>
      </div>
    </div>
  );
};

export const CartRecommendation = () => {
  const [showAddOnModal, setShowAddOnModal] = useState(false);
  const [product, setProduct] = useState<StoreProductModel | null>(null);
  const [showAllAddOns, setShowAllAddOns] = useState(false);

  const { storeProducts } = useStore();
  const { cartItems, validateCart } = useCart();

  const cartProductIdsKey = useMemo(
    () =>
      cartItems
        .map((i) => i.product_id)
        .sort()
        .join(","),
    [cartItems],
  );

  const availableProductIdsKey = useMemo(() => {
    const inCart = new Set(
      cartProductIdsKey ? cartProductIdsKey.split(",") : [],
    );
    return storeProducts
      .filter((p) => p.modifier_group.length === 0 && !inCart.has(p.id))
      .map((p) => p.id)
      .sort()
      .join(",");
  }, [storeProducts, cartProductIdsKey]);

  const randomProducts = useMemo(() => {
    const idSet = new Set(
      availableProductIdsKey ? availableProductIdsKey.split(",") : [],
    );
    const available = storeProducts.filter((p) => idSet.has(p.id));
    return [...available].sort(() => Math.random() - 0.5).slice(0, 5);
  }, [storeProducts, availableProductIdsKey]);

  const addonProducts = useMemo(() => {
    const inCart = new Set(
      cartProductIdsKey ? cartProductIdsKey.split(",") : [],
    );

    return storeProducts.filter(
      (p) => p.modifier_group.length === 0 && !inCart.has(p.id),
    );
  }, [storeProducts, cartProductIdsKey]);

  if (cartItems.length === 0 || randomProducts.length === 0) return null;

  const handleClose = () => {
    setShowAddOnModal(false);
  };

  const handleSubmit = () => {
    validateCart();
    setShowAddOnModal(false);
  };

  return (
    <>
      <div className="cart-suggestions">
        <div className="d-flex justify-content-between align-items-center">
          <h4 className="cart-suggestions-title">Popular add-ons</h4>
          <button
            type="button"
            className="cart-suggestions-title text-decoration-underline border-0 bg-transparent p-0"
            onClick={() => setShowAllAddOns(true)}
          >
            View all
          </button>
        </div>

        <div className="cart-suggestions-slider">
          <Slider {...sliderSettings}>
            {randomProducts.map((item) => (
              <CartSuggestionCard
                key={item.id}
                item={item}
                onAddClick={(product) => {
                  setProduct(product);
                  setShowAddOnModal(true);
                }}
              />
            ))}
          </Slider>
        </div>
      </div>

      <Modal
        show={showAllAddOns}
        onHide={() => setShowAllAddOns(false)}
        centered
        scrollable
        backdropClassName="cart-addon-backdrop"
        className="cart-addon-modal modal-mobile-bottom cart_aad_on"
        dialogClassName="cart-addon-modal-dialog"
      >
        <Modal.Header closeButton>
          <Modal.Title>Popular Add-ons</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="addon-grid">
            {addonProducts.length > 0 ? (
              addonProducts.map((item) => (
                <AddonGridCard
                  key={item.id}
                  item={item}
                  onAddClick={(product) => {
                    setShowAllAddOns(false);
                    setProduct(product);
                    setShowAddOnModal(true);
                  }}
                />
              ))
            ) : (
              <div className="addon-grid-empty">
                No add-ons available right now.
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>

      {product && (
        <ProductAddOnModal
          product={product}
          show={showAddOnModal}
          handleSubmit={handleSubmit}
          handleClose={handleClose}
        />
      )}
    </>
  );
};
